import {Component, OnInit} from '@angular/core';
import {ModalInteractiveComponent} from "../../layout/dialogs/modal-interactive/modal-interactive.component";
import {MatDialog} from "@angular/material/dialog";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule, Validators,
} from "@angular/forms";
import {CardPayment, CardPaymentForm} from "../../core/interface/card-payment";
import {NgClass} from "@angular/common";
import {ValidatorDateDirective} from "../../core/directive/validators-datepicker.directive";
import {Router} from "@angular/router";
import {PaymentService} from "../../core/services/payment/payment.service";
import {MoveToNextDirective} from "../../core/directive/moveToNextInput.directive";
import {formatTransactionDate} from "./date/date-utils";

@UntilDestroy()
@Component({
  selector: 'payment-main',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ValidatorDateDirective,
    MoveToNextDirective,
  ],
  standalone: true,
  templateUrl: './payment-main.component.html',
  styleUrl: './payment-main.component.scss'
})
export class PaymentMainComponent implements OnInit{
  formCardInfo: FormGroup<CardPaymentForm> = new FormGroup<CardPaymentForm>({
    maskedPan: new FormControl<string | null | undefined>(null,[
      Validators.required,
      Validators.minLength(16),
    ]),
    cardExpDate: new FormControl<string | null | undefined>('', [
      Validators.required,
      Validators.pattern(/^20\d{2}-(0[1-9]|1[0-2])$/),
      Validators.minLength(5)
    ],),
    firstTransactionNumber: new FormControl<string | null | undefined>(null,[
      Validators.required,
      Validators.minLength(16),
    ]),
    firstTransactionDate: new FormControl<string | null | undefined>(null,[
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(10)
    ]),
    sum: new FormControl<number | null | undefined>(null, [
      Validators.required,
      Validators.pattern(/^\d+([.,]\d+)?$/),
      Validators.min(1)
    ]),
    captchaToken: new FormControl<string | null | undefined>(null),
    cardExpMonth: new FormControl('', [
      Validators.required,
      Validators.pattern(/^(0[1-9]|1[0-2])$/)
    ]),
    cardExpYear: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d{2}$/)
    ]),
    maskedPanBeginning: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d{6}$/)
    ]),
    maskedPanEnd: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d{4}$/)
    ]),
  });
  constructor(private router: Router,
              private paymentService: PaymentService,
              private dialog: MatDialog) {}

  ngOnInit() {
    this.loadScript().then(() => this.loadCaptcha());
    this.updateFormMergingValues();
    this.resetValidatorsIfNeeded()
  }

  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('smartcaptcha-script')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = 'smartcaptcha-script';
      script.src = 'https://smartcaptcha.yandexcloud.net/captcha.js';
      script.onload = () => resolve();
      script.onerror = () => reject('Ошибка загрузки SmartCaptcha');
      document.body.appendChild(script);
    });
  }
  loadCaptcha() {
    if ((window as any).smartCaptcha) {
      (window as any).smartCaptcha.render('captcha-container', {
        sitekey: 'ysc1_OZVCk9got36ReJy5lqOMBqzKqyGrf1FkWctGfmjB6da8f8d3',
        invisible: true,
        shieldPosition: 'bottom-right',
        callback: (response: string) => {
          this.formCardInfo.get('captchaToken')?.setValue(response);
          this.getInfoCard()
        },
        hideShield: false,
      });
    } else {
      console.error('SmartCaptcha не загружена');
    }
  }

  submitForm() {
    if (this.formCardInfo.valid) {
      if ((window as any).smartCaptcha) {
        (window as any).smartCaptcha.execute();
      } else {
        console.error('SmartCaptcha не загружена');
      }
    } else {
      console.warn('Форма заполнена некорректно');
    }
  }

  resetValidatorsIfNeeded(): void {
    this.formCardInfo.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.formCardInfo.get('maskedPan')?.value
        && this.formCardInfo.get('cardExpDate')?.value
        && this.formCardInfo.get('firstTransactionDate')?.value
        && this.formCardInfo.get('sum')?.value) {
        Object.keys(this.formCardInfo.controls).forEach((key) => {
          if (!['maskedPan', 'cardExpDate', 'firstTransactionDate', 'sum'].includes(key)) {
            this.formCardInfo.get(key)?.clearValidators();
            this.formCardInfo.get(key)?.updateValueAndValidity({ emitEvent: false });
          }
        });
      } else if (
        this.formCardInfo.get('maskedPan')?.value
        && this.formCardInfo.get('cardExpDate')?.value
        && this.formCardInfo.get('firstTransactionNumber')?.value) {
        Object.keys(this.formCardInfo.controls).forEach((key) => {
          if (!['maskedPan', 'cardExpDate', 'firstTransactionNumber'].includes(key)) {
            this.formCardInfo.get(key)?.clearValidators();
            this.formCardInfo.get(key)?.updateValueAndValidity({ emitEvent: false });
          }
        });
      } else {
        // Если ни одно условие не прошло, восстанавливаем валидаторы
        Object.keys(this.formCardInfo.controls).forEach((key) => {
          this.formCardInfo.get(key)?.setValidators(
            {
              cardExpMonth: [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])$/)],
              cardExpYear: [Validators.required, Validators.pattern(/^\d{2}$/)],
              maskedPanBeginning: [Validators.required, Validators.pattern(/^\d{6}$/)],
              maskedPanEnd: [Validators.required, Validators.pattern(/^\d{4}$/)],
              maskedPan: [Validators.required, Validators.minLength(16)],
              cardExpDate: [Validators.required,  Validators.pattern(/^20\d{2}-(0[1-9]|1[0-2])$/), Validators.minLength(5)],
              firstTransactionNumber: [Validators.required, Validators.minLength(16)],
              firstTransactionDate: [Validators.required, Validators.minLength(10), Validators.maxLength(10)],
              captchaToken: [],
              sum: [
                Validators.required,
                Validators.pattern(/^\d+([.,]\d+)?$/),
                Validators.min(1)
              ]
            }[key] || []);
          this.formCardInfo.get(key)?.updateValueAndValidity({ emitEvent: false });
        });
      }
    });
  }




  getInfoCard(){
    const { maskedPanEnd, maskedPanBeginning, cardExpMonth, cardExpYear, firstTransactionDate, ...formCard } = this.formCardInfo.value;
    const formCardInfo = {
      ...formCard,
      firstTransactionDate: formatTransactionDate(firstTransactionDate)
    } as CardPayment;
    this.paymentService.getInfoCard(formCardInfo)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (infoCard) => {
          if (infoCard.status === 200) {
            this.router.navigate(['/search']);
          }
        },
        error: (err) => {
          console.error('Ошибка при получении информации о карте:', err);
        }
      });
  };

  updateFormMergingValues() {
    this.formCardInfo.valueChanges.pipe(untilDestroyed
    (this)).subscribe(() => {
      if (this.formCardInfo.get('cardExpMonth')?.value && this.formCardInfo.get('cardExpYear')?.value) {
        this.formCardInfo.get('cardExpDate')?.setValue(`20${this.formCardInfo.get('cardExpYear')?.value}-${this.formCardInfo.get('cardExpMonth')?.value}`, { emitEvent: false });
      } else {
        this.formCardInfo.get('cardExpDate')?.setValue(null, { emitEvent: false });
      }
      if (this.formCardInfo.get('maskedPanBeginning')?.value && this.formCardInfo.get('maskedPanEnd')?.value) {
        this.formCardInfo.get('maskedPan')?.setValue(`${this.formCardInfo.get('maskedPanBeginning')?.value?.replace(/\s+/g, '')}xxxxxx${this.formCardInfo.get('maskedPanEnd')?.value}`, { emitEvent: false });
      } else {
        this.formCardInfo.get('maskedPan')?.setValue(null, { emitEvent: false });
      }
    });
  }
  openModalInteractive() {
    this.dialog.open(ModalInteractiveComponent, {
      disableClose: true,
    });
  }

}
